/**
 * src/lib/ipcFinancials.js
 * ─────────────────────────────────────────────────────────────────────────
 * SHARED DOMAIN BUSINESS LOGIC — CostSpace
 *
 * Purpose (general, NOT tied to any single screen or report):
 *   Computes the financial totals for a single IPC (Interim Payment
 *   Certificate) — gross, VAT, retention (with cap and Final-IPC true-up),
 *   advance recovery (capped at outstanding advance), and net payable —
 *   from raw certificate/line/contract inputs.
 *
 *   This is domain logic, not screen logic. Any current or future IPC
 *   creation path (Wizard, bulk Excel upload, future API/integration) must
 *   call this module instead of re-deriving the calculation locally.
 *
 * Background (Phase 1 of the IPC remediation architecture, approved
 * following a multi-session audit):
 *   Prior to this module, IPC financial calculation existed independently
 *   in FOUR places inside IPC.jsx:
 *     1. wizardTotals()          — Material, interactive creation wizard
 *     2. eqWizardTotals()        — Equipment, interactive creation wizard
 *        (confirmed structurally identical to #1 by direct line-by-line
 *        comparison — the only difference was data source, not formula)
 *     3. confirmIpcUpload()      — Material, bulk Excel upload (inline,
 *        unnamed calculation block)
 *     4. eqConfirmIpcUpload()    — Equipment, bulk Excel upload (same
 *        inline pattern as #3)
 *
 *   Paths #3/#4 implemented a SIMPLIFIED retention formula that omitted
 *   the retention cap (retention_pct × contract_value) and the
 *   prevRetentionHeld subtraction entirely — confirmed by a concrete
 *   numeric audit test to produce a DIFFERENT net_payable than paths #1/#2
 *   for the same inputs whenever cumulative retention approaches the cap
 *   (test case: prevRetentionHeld=97,000 against a 100,000 cap produced
 *   net=95,000 via the old upload-path formula vs. the correct net=102,000
 *   via the wizard formula — a confirmed 7,000 discrepancy, not a
 *   theoretical one). This module exists specifically to close that gap by
 *   giving all four paths one shared, correct calculation.
 *
 * Design constraints (strict — matches contractFinancials.js/forecastEAC.js):
 *   - 100% pure functions. No Supabase import, no React state, no Context,
 *     no Date.now()/today() fallback, no other global/ambient state.
 *   - Deterministic: same inputs always produce the same outputs.
 *   - All data access (fetching contract/prior-IPC data) remains the
 *     responsibility of the caller. This module only computes from
 *     already-fetched, already-resolved values.
 *
 * Explicit Phase 1 scope note:
 *   This module covers IPC CREATION-time calculation only
 *   (computeIpcFinancials, consumed by wizardSave/eqWizardSave/
 *   confirmIpcUpload/eqConfirmIpcUpload). It intentionally does NOT cover
 *   IPC.jsx's separate DISPLAY-time functions (computeIpcTotals/
 *   computeEqIpcTotals), which read stored values for an already-saved
 *   IPC rather than computing them fresh. Those two functions are
 *   unchanged in Phase 1 per the approved scope — a previously-confirmed,
 *   display-only discrepancy in their `outstanding` field (never part of
 *   net_payable) remains open and out of scope here.
 *
 *   This module also intentionally does NOT derive an IPC's period
 *   (period_month) from period_from/period_to — that responsibility
 *   belongs to a separate function planned for Phase 2, once Excel
 *   template changes are approved. Phase 1 does not call it from any path.
 * ─────────────────────────────────────────────────────────────────────────
 */

/**
 * Phase 2 addition — derivePeriodMonth()
 * ─────────────────────────────────────────────────────────────────────────
 * SSOT for deriving an IPC's period_month from its certification period
 * (period_from / period_to). Consumed by BOTH the interactive Wizard and
 * the bulk Excel upload paths — no caller may derive period_month with its
 * own logic.
 *
 * Business rule (confirmed from the pre-existing, unchanged Wizard logic):
 *   src = periodFrom || periodTo
 *   period_month = src.substring(0, 7) + '-01'
 *
 * Strict design constraint: this function NEVER falls back to today's date
 * or any other invented value. If both periodFrom and periodTo are
 * missing/invalid, it returns a validation failure instead of silently
 * inventing a period. This is the exact bug this function exists to
 * prevent — a bulk-uploaded historical IPC must never be silently
 * attributed to the upload date's month.
 *
 * The Wizard's own user-facing convenience fallback (defaulting to today()
 * when both fields are empty in the interactive form) is intentionally
 * NOT implemented here — that fallback belongs to the Wizard's UI layer,
 * not to this centralized business-rule function, so the business rule
 * itself stays strict for every caller (Excel included) while the Wizard
 * may still choose to apply its own UI-level convenience on top of a
 * validation failure from this function.
 *
 * @param {string|null} periodFrom - Expected format: 'YYYY-MM-DD', or null/empty
 * @param {string|null} periodTo - Expected format: 'YYYY-MM-DD', or null/empty
 *
 * @returns {
 *   { valid: true, periodMonth: string } |
 *   { valid: false, error: string }
 * }
 */
export function derivePeriodMonth(periodFrom, periodTo) {
  const src = periodFrom || periodTo
  if (!src) {
    return { valid: false, error: 'Both period_from and period_to are missing — cannot derive an IPC period.' }
  }
  // Expect an unambiguous YYYY-MM-DD string at this point — the caller
  // (Wizard form input, or the Excel date parser) is responsible for
  // normalizing to this format before calling this function. This
  // function does not itself parse ambiguous date formats.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(src)) {
    return { valid: false, error: `Invalid period date format: "${src}" — expected YYYY-MM-DD.` }
  }
  return { valid: true, periodMonth: src.substring(0, 7) + '-01' }
}

/**
 * Computes the full financial position for a single IPC from already-
 * resolved inputs. Owns every calculated field that gets written to
 * ipc_certificates at creation time — the caller must not recompute or
 * override any of the returned fields before insert.
 *
 * @param {Object} inputs
 * @param {number} inputs.gross - Sum of this-period line amounts (exc. VAT)
 * @param {number} inputs.retPct - Retention percentage for this IPC (0-100)
 * @param {number} inputs.contractValue - The contract's total value
 * @param {boolean} inputs.isFinal - Whether this is the contract's Final IPC
 * @param {number} inputs.prevRetentionHeld - Sum of retention_amount (falling
 *   back to retention_held) across this contract's prior approved/paid IPCs
 * @param {number} inputs.prevCertifiedForRet - Sum of gross_amount across
 *   this contract's prior approved/paid IPCs
 * @param {number} inputs.advRecoveryRate - Advance recovery rate (0-100)
 * @param {number} inputs.realAdvPay - Actual recorded advance payment for
 *   this contract, if any (0 if none recorded yet)
 * @param {number} inputs.advPct0 - Contract's advance percentage, used as a
 *   fallback total-advance basis only when realAdvPay is 0
 * @param {number} inputs.prevRecoveries - Sum of advance_recovery across ALL
 *   of this contract's prior IPCs (no status filter — matches the existing,
 *   confirmed behavior of both wizardTotals() and the old upload-path logic)
 * @param {number} inputs.vatCase - 1 or 2; case 2 additionally applies VAT to
 *   the advance recovery amount
 * @param {number} inputs.vatRate - VAT rate as a decimal fraction (e.g. 0.15)
 * @param {number} inputs.totalDeductions - Sum of any additional deduction
 *   line amounts for this IPC (0 if none)
 *
 * @returns {{
 *   gross: number, vat: number, totalIncVat: number,
 *   ret: number, retentionCap: number,
 *   advRecovery: number, advRecoveryVat: number, totalAdvIncVat: number,
 *   outstanding: number, totalAdvPay: number,
 *   net: number,
 *   prevCertified: number, cumulativeCertified: number,
 *   remainingContractValue: number, overContractValue: boolean,
 * }}
 */
export function computeIpcFinancials(inputs) {
  const {
    gross, retPct, contractValue, isFinal,
    prevRetentionHeld, prevCertifiedForRet,
    advRecoveryRate, realAdvPay, advPct0, prevRecoveries,
    vatCase, vatRate, totalDeductions,
  } = inputs

  const vat = +(gross * vatRate).toFixed(2)
  const totalIncVat = +(gross + vat).toFixed(2)

  // Retention — capped at retention_pct × contract_value (never over-withholds),
  // never negative cumulative, and on the Final IPC, true-up to close exactly
  // instead of applying the normal per-line % again.
  const retentionCap = +(contractValue * retPct / 100).toFixed(2)
  let ret
  if (isFinal) {
    const cumulativeGrossFinal = prevCertifiedForRet + gross
    const targetCumulativeRet = +(cumulativeGrossFinal * retPct / 100).toFixed(2)
    ret = +Math.max(0, targetCumulativeRet - prevRetentionHeld).toFixed(2)
  } else {
    const rawRet = +(gross * retPct / 100).toFixed(2)
    const retOutstandingCap = retentionCap > 0 ? Math.max(0, retentionCap - prevRetentionHeld) : Infinity
    ret = rawRet >= 0
      ? +Math.min(rawRet, retOutstandingCap).toFixed(2)
      : +Math.max(rawRet, -prevRetentionHeld).toFixed(2) // negative adjustment never drops cumulative below 0
  }

  // Advance recovery — computed from adv_recovery_rate, but never exceeds the
  // outstanding advance. Advance ceiling: use the real Finance-recorded
  // advance payment; if none exists yet, fall back to advance_pct × contract_value.
  const totalAdvPay = realAdvPay > 0 ? realAdvPay : +(contractValue * advPct0 / 100).toFixed(2)
  const outstanding = Math.max(0, totalAdvPay - prevRecoveries)
  const rawRecovery = +(gross * advRecoveryRate / 100).toFixed(2)
  const advRecovery = +Math.min(rawRecovery, outstanding).toFixed(2)

  let advRecoveryVat = 0
  let totalAdvIncVat = advRecovery
  let net
  if (vatCase === 2) {
    advRecoveryVat = +(advRecovery * vatRate).toFixed(2)
    totalAdvIncVat = +(advRecovery + advRecoveryVat).toFixed(2)
    net = +(totalIncVat - ret - advRecovery - advRecoveryVat - totalDeductions).toFixed(2)
  } else {
    net = +(totalIncVat - ret - advRecovery - totalDeductions).toFixed(2)
  }

  // Contract Value Ceiling info
  const prevCertified = prevCertifiedForRet
  const cumulativeCertified = +(prevCertified + gross).toFixed(2)
  const remainingContractValue = +(contractValue - prevCertified).toFixed(2)
  const overContractValue = contractValue > 0 && cumulativeCertified > contractValue

  return {
    gross, vat, totalIncVat,
    ret, retentionCap,
    advRecovery, advRecoveryVat, totalAdvIncVat, outstanding, totalAdvPay,
    net,
    prevCertified, cumulativeCertified, remainingContractValue, overContractValue,
  }
}
